import { mdiCloseCircle, mdiDownload, mdiFileEdit, mdiPaperclip, mdiShapeSquarePlus } from '@mdi/js';
import React, { useState } from 'react';
import { formatBytes } from '../../services';
import * as S from './attachment-button.styles';

export interface IAttachmentButtonProps {
    className?: string;
    filename: string;
    filesize?: number;
    thumbnail?: string;
    onDownload?: () => Promise<void>; // This is actually for viewing/opening the document
    onActualDownload?: () => Promise<string | void>; // This returns the download URL
    downloadUrl?: string;
    documentId?: string;
    onRemove?: () => void;
    onAssign?: () => void;
    onEdit?: () => Promise<void> | void;
    isSupportApp?: boolean;
}
export const AttachmentButton: React.FunctionComponent<IAttachmentButtonProps> = (
    props: IAttachmentButtonProps,
): React.ReactElement => {
    const { className, filename, filesize, thumbnail, onDownload, onActualDownload, downloadUrl, documentId, onRemove, onAssign, onEdit, isSupportApp } = props;

    const [isDownloading, setIsDownloading] = useState(false);
    const [isAssigning, setIsAssigning] = useState(false);
    const [isAssigned, setIsAssigned] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const showSupportActions = isSupportApp === true;
    const showAssignButton = showSupportActions && !!onAssign && !isAssigned;
    const showEditButton = showSupportActions && !!onEdit;

    const handleDownload = async (): Promise<void> => {
        setIsDownloading(true);
        await (onDownload && onDownload());
        setIsDownloading(false);
    };

    const handleAssign = async (e: React.MouseEvent): Promise<void> => {
        e.stopPropagation();
        setIsAssigning(true);
        await (onAssign && onAssign());
        setIsAssigning(false);
        setIsAssigned(true);
    };

    const handleEdit = async (e: React.MouseEvent): Promise<void> => {
        e.stopPropagation();
        if (!onEdit) {
            return;
        }
        setIsEditing(true);
        try {
            await Promise.resolve(onEdit());
        } finally {
            setIsEditing(false);
        }
    };

    const handleRemove = async (e: React.MouseEvent): Promise<void> => {
        e.stopPropagation();
        setIsRemoving(true);
        await (onRemove && onRemove());
        setIsRemoving(false);
    };

    const handleAttachmentClick = async (): Promise<void> => {
        // This opens/views the document
        if (onDownload) {
            await handleDownload();
        }
    };

    const handleDownloadClick = async (e: React.MouseEvent): Promise<void> => {
        e.stopPropagation();
        setIsDownloading(true);
        
        try {
            let urlToDownload = downloadUrl;
            
            // If we have an onActualDownload function, call it to get the download URL
            if (!urlToDownload && onActualDownload) {
                const result = await onActualDownload();
                if (typeof result === 'string') {
                    urlToDownload = result;
                }
            }
            
            // If we have a URL, use direct HTML download
            if (urlToDownload) {
                // Create an anchor element for download
                const link = document.createElement('a');
                link.href = urlToDownload;
                link.download = filename;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                
                // Trigger download
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } finally {
            setIsDownloading(false);
        }
    };

    const renderDownloadButton = () => {
        // Show download button if we can download
        if (!downloadUrl && !onActualDownload) {
            return null;
        }

        return (
            <S.StyledTooltip direction="top" variant="dark" title={<span>Download</span>}>
                <S.IconButton onClick={handleDownloadClick} disabled={isDownloading} variant="text">
                    <S.DownloadIcon path={mdiDownload} size={0.75} />
                </S.IconButton>
            </S.StyledTooltip>
        );
    };

    const renderEditButton = () => {
        if (!showEditButton) {
            return null;
        }

        return (
            <S.StyledTooltip direction="top" variant="dark" title={<span>Edit Document Information</span>}>
                <S.IconButton onClick={handleEdit} disabled={isEditing} variant="text">
                    <S.EditIcon path={mdiFileEdit} size={0.75} />
                </S.IconButton>
            </S.StyledTooltip>
        );
    };

    const renderAssignButton = () => {
        if (!showAssignButton) {
            return null;
        }

        return (
            <S.StyledTooltip direction="top" variant="dark" title={<span>Assign document to the claim(s)</span>}>
                <S.IconButton onClick={handleAssign} disabled={isAssigning} variant="text">
                    <S.CloseIcon path={mdiShapeSquarePlus} size={0.75} />
                </S.IconButton>
            </S.StyledTooltip>
        );
    };

    const renderImage = () => {
        return (
            <S.AttachmentImage title={filename}>
                <S.AttachmentThumbnail src={thumbnail} alt={filename} onClick={handleDownload} />
                {renderDownloadButton()}
                {renderEditButton()}
                {renderAssignButton()}
                {onRemove && (
                    <S.IconButton onClick={handleRemove} variant="text" disabled={isRemoving}>
                        <S.CloseIcon path={mdiCloseCircle} size={0.75} />
                    </S.IconButton>
                )}
                {isDownloading && <S.DownloadSpinner size="18px" />}
            </S.AttachmentImage>
        );
    };
    const renderButton = () => {
        return (
            <S.AttachmentButton className={className} onClick={handleAttachmentClick}>
                <S.PaperclipIcon path={mdiPaperclip} size={0.75} />
                <S.AttachmentLabels>
                    <S.AttachmentFilename>{filename}</S.AttachmentFilename>
                    <S.AttachmentSize>{formatBytes(filesize)}</S.AttachmentSize>
                </S.AttachmentLabels>
                {renderDownloadButton()}
                {renderEditButton()}
                {renderAssignButton()}
                {onRemove && (
                    <S.IconButton onClick={handleRemove} variant="text" disabled={isRemoving}>
                        <S.CloseIcon path={mdiCloseCircle} size={0.75} />
                    </S.IconButton>
                )}
                {isDownloading && <S.DownloadSpinner size="18px" />}
            </S.AttachmentButton>
        );
    };

    return thumbnail ? renderImage() : renderButton();
};
